import React, { useState } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
  ListItemText,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import { InboxItem } from '../types';

export interface AssignDropdownProps {
  item: InboxItem;
  hangars: Array<{ id: string; name: string; width?: number; depth?: number; height?: number }>;
  ramps: Array<{ id: string; name: string }>;
  onAssignToHangar: (hangarId: string, hangarName: string) => Promise<void>;
  onAssignToRamp: (rampId: string, rampName: string) => Promise<void>;
  isAssigning?: boolean;
}

/**
 * Check if aircraft fits in hangar
 */
function canFitInHangar(
  item: InboxItem,
  hangar: { width?: number; depth?: number }
): { fits: boolean; reason?: string } {
  if (!item.wingspan || !item.aircraft_length) {
    return { fits: true }; // Allow if we don't have aircraft dimensions
  }

  if (hangar.width && item.wingspan > hangar.width) {
    return { fits: false, reason: `Wingspan ${item.wingspan}' > ${hangar.width}'` };
  }

  if (hangar.depth && item.aircraft_length > hangar.depth) {
    return { fits: false, reason: `Length ${item.aircraft_length}' > ${hangar.depth}'` };
  }

  return { fits: true };
}

/**
 * Format hangar dimensions
 */
function formatHangarDimensions(
  hangar: { width?: number; depth?: number; height?: number },
  t: (key: string, params?: any) => string
): string {
  const parts = [];
  if (hangar.width) parts.push(t('screens.operationsInbox.assignDropdown.dimensions.width', { width: hangar.width }));
  if (hangar.depth) parts.push(t('screens.operationsInbox.assignDropdown.dimensions.depth', { depth: hangar.depth }));
  if (hangar.height) parts.push(t('screens.operationsInbox.assignDropdown.dimensions.height', { height: hangar.height }));
  return parts.join(' × ') || t('screens.operationsInbox.assignDropdown.noDimensions');
}

/**
 * Dropdown for assigning trips to hangars or ramps
 */
export function AssignDropdown({
  item,
  hangars,
  ramps,
  onAssignToHangar,
  onAssignToRamp,
  isAssigning = false,
}: AssignDropdownProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleAssignToHangar = async (hangarId: string, hangarName: string) => {
    setOpen(false);
    setSearchQuery('');
    await onAssignToHangar(hangarId, hangarName);
  };

  const handleAssignToRamp = async (rampId: string, rampName: string) => {
    setOpen(false);
    setSearchQuery('');
    await onAssignToRamp(rampId, rampName);
  };

  // Filter by search query
  const filteredHangars = hangars.filter(h =>
    h.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredRamps = ramps.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <Select
        disabled={isAssigning}
        value=""
        displayEmpty
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchQuery('');
        }}
        renderValue={() => t('screens.operationsInbox.assignDropdown.assignTo')}
        data-testid={`assign-dropdown-${item.item_id}`}
        MenuProps={{ autoFocus: false }}
        sx={{
          fontSize: 13,
          '& .MuiSelect-select': { py: 0.75 },
        }}
      >
        {/* Search Filter */}
        {(hangars.length + ramps.length) > 5 && (
          <ListSubheader sx={{ py: 1, px: 2 }}>
            <TextField
              size="small"
              placeholder={t('screens.operationsInbox.assignDropdown.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => {
                e.stopPropagation();
                setSearchQuery(e.target.value);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: -0.5 }}>
                    <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                  fontSize: 13,
                  '& input': { py: 0.75 },
                },
              }}
            />
          </ListSubheader>
        )}

        {/* Hangars Section */}
        {filteredHangars.length > 0 && (
          <ListSubheader sx={{ fontSize: 11, fontWeight: 700 }}>
            {t('screens.operationsInbox.assignDropdown.hangarsSection')}
          </ListSubheader>
        )}
        {filteredHangars.map(hangar => {
          const validation = canFitInHangar(item, hangar);
          return (
            <MenuItem
              key={hangar.id}
              disabled={!validation.fits}
              onClick={() => validation.fits && handleAssignToHangar(hangar.id, hangar.name)}
              sx={{ py: 1.5 }}
            >
              <ListItemText
                primary={hangar.name}
                secondary={formatHangarDimensions(hangar, t)}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
              {!validation.fits && (
                <Chip
                  label={t('screens.operationsInbox.assignDropdown.tooSmall')}
                  size="small"
                  color="error"
                  sx={{ ml: 1, height: 20, fontSize: 10 }}
                />
              )}
            </MenuItem>
          );
        })}

        {/* Ramps Section */}
        {filteredRamps.length > 0 && (
          <ListSubheader sx={{ fontSize: 11, fontWeight: 700, mt: 1 }}>
            {t('screens.operationsInbox.assignDropdown.rampsSection')}
          </ListSubheader>
        )}
        {filteredRamps.map(ramp => (
          <MenuItem
            key={ramp.id}
            onClick={() => handleAssignToRamp(ramp.id, ramp.name)}
            sx={{ py: 1.5 }}
          >
            <ListItemText
              primary={ramp.name}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
            />
          </MenuItem>
        ))}

        {filteredHangars.length === 0 && filteredRamps.length === 0 && (
          <MenuItem disabled>
            <ListItemText
              primary={
                searchQuery
                  ? t('screens.operationsInbox.assignDropdown.noMatchingLocations')
                  : t('screens.operationsInbox.assignDropdown.noLocationsAvailable')
              }
              primaryTypographyProps={{ fontSize: 13, fontStyle: 'italic' }}
            />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}
